import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DEFAULT_WEEK_START, type WeekStartDay } from '@utils/userConfig';

// Define a type for the slice state
export interface AuthState {
  token: string | null;
  // SimpleJWT refresh token. Used to obtain a new access token (auth/refresh) and
  // to blacklist the session on logout (auth/logout).
  refresh: string | null;
  scopes: string[] | null;
  role: number | null;
  // Real (token-owning) user's impersonation capability, resolved from the login
  // scopes. Kept separate from `scopes`/`role` because those are overwritten with
  // the *effective* (impersonated) user by `QuickUserToggler`; this stays true for
  // the admin throughout an impersonation session (and across reloads, since it is
  // persisted), which is what keeps the impersonation banner working.
  canImpersonate: boolean;
  usersGridConfig: any | null;
  userId: string | null;
  segmentationTypes: string[] | null;
  // First-day-of-week (FullCalendar `firstDay`), hydrated from `User.config` by
  // `QuickUserToggler` and persisted so calendars have a value before `useMe` resolves.
  weekStart: WeekStartDay;
}

// Define the initial state using that type
export const authInitialState: AuthState = {
  token: null,
  refresh: null,
  scopes: null,
  role: null,
  canImpersonate: false,
  usersGridConfig: null,
  userId: null,
  segmentationTypes: null,
  weekStart: DEFAULT_WEEK_START,
};

/**
 * One persisted per-user grid configuration, as the store holds it. Declared here because the
 * upsert below has to identify an entry; the rest of the record stays opaque to the slice.
 */
export type StoredGridConfig = { id: string } & Record<string, unknown>;

export const AuthSlice = createSlice({
  name: 'auth',
  initialState: authInitialState,
  reducers: {
    setAuth: (_, action: PayloadAction<AuthState>) => action.payload,
    setUserConfig: (
      state,
      action: PayloadAction<{
        userId?: string | null;
        usersGridConfig?: any | null;
        segmentationTypes?: string[] | null;
        weekStart?: WeekStartDay;
      }>
    ) => {
      const { userId, usersGridConfig, segmentationTypes, weekStart } = action.payload;
      if (typeof userId !== 'undefined') state.userId = userId;
      if (typeof usersGridConfig !== 'undefined') state.usersGridConfig = usersGridConfig;
      if (typeof segmentationTypes !== 'undefined') state.segmentationTypes = segmentationTypes;
      if (typeof weekStart !== 'undefined') state.weekStart = weekStart;
    },
    // Writes one grid's configuration without the caller having to rebuild the whole list. A
    // caller that sends the list instead sends the copy it last rendered, and a response can land
    // when there is no render left to refresh it — after the grid unmounted — so that copy silently
    // drops whatever was stored in between, including the entry the same request just created.
    //
    // `userId` is who the request was sent as. A grid stays mounted across a change of effective
    // user — ending an impersonation in another tab reinstalls the list through `setUserConfig` —
    // so a write already travelling can land against someone else's slice. Storing it there would
    // hand the new user the previous one's configuration id, and every save after it would PATCH a
    // record the backend scopes to its owner. Checked here because the live id lives here; a
    // caller can only offer the closure React Query has since refreshed.
    upsertUsersGridConfig: (
      state,
      action: PayloadAction<{ userId: string; entry: StoredGridConfig }>
    ) => {
      const { userId, entry } = action.payload;
      if (userId !== state.userId) return;
      const stored: unknown = state.usersGridConfig;
      const list: StoredGridConfig[] = Array.isArray(stored) ? stored : [];
      const index = list.findIndex((config) => config?.id === entry.id);
      state.usersGridConfig =
        index === -1 ? [...list, entry] : list.map((config, at) => (at === index ? entry : config));
    },
  },
});

export default AuthSlice;
